import { Fragment, useEffect, useRef, useState } from 'react';
import { useHistory } from "react-router-dom"
import { DonutRing } from "../atoms"
import { BENEFIT_TYPE_MAP, SERVICE_CARD_DETAILS, formatCardTileLabel } from '../../../constants/overview';
import { DownloadPngButton } from '../../opdClaims/primitives';
import { handleAppError } from "../../handlers"
import { commaingNumber, computeRating } from "../../../utils"
import { SpinnerDotted } from "../../common/atoms"

/** Service accent colors from the Aayuv DS data-viz palette. */
const SERVICE_DONUT_COLORS = {
  health_checks: '#14866D',
  consultations: '#1D6FA4',
  dental: '#D97706',
  vision: '#D97706',
  hra: '#7C3AED',
};

/** Per-service monogram tile from the 2a design — colors are DS service tints (runtime data, like DonutRing). */
const SERVICE_MONOGRAMS = {
  health_checks: { text: 'HC', bg: '#E8F3F0', fg: '#0E6354' },
  hra: { text: 'HRA', bg: '#F2EBFD', fg: '#5B21B6' },
  pre_employment: { text: 'PE', bg: '#E8F1F6', fg: '#155884' },
  consultations: { text: 'GP', bg: '#E8F1F6', fg: '#155884' },
  dental: { text: 'DN', bg: '#FDF1E4', fg: '#92400E' },
  vision: { text: 'VI', bg: '#FDF1E4', fg: '#92400E' },
};

const ServiceMonogram = ({ service, className = '' }) => {
  const m = SERVICE_MONOGRAMS[service] ?? { text: (service || '?').slice(0, 2).toUpperCase(), bg: '#E8F1F6', fg: '#155884' };
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-[10px] font-jetbrains-mono font-semibold ${m.text.length > 2 ? 'text-[10px]' : 'text-[11px]'} ${className}`}
      style={{ width: 38, height: 38, background: m.bg, color: m.fg }}
      aria-hidden
    >
      {m.text}
    </span>
  );
};

const StatBlock = ({ label, value, caption, linkText, onLink }) => (
  <div className='min-w-0'>
    <p className='mb-0 text-[11px] font-semibold uppercase tracking-[.06em] text-gray-600'>{label}</p>
    <div className='mt-1 font-jetbrains-mono text-[22px] font-semibold leading-tight tabular-nums text-gray-900'>{value}</div>
    {caption ? <p className='mb-0 mt-1 text-[11px] leading-snug text-gray-600'>{caption}</p> : null}
    {linkText ? (
      <button
        type='button'
        className='mt-2 inline-flex cursor-pointer border-0 bg-transparent p-0 text-[11px] font-semibold text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 rounded-sm'
        onClick={onLink}
      >
        {linkText} →
      </button>
    ) : null}
  </div>
);

const SplitStat = ({ value1, liner1, value2, liner2 }) => (
  <div className='flex items-center gap-4'>
    <div>
      <div className='font-jetbrains-mono text-xl font-semibold tabular-nums leading-tight text-gray-900'>{value1}</div>
      <p className='mb-0 mt-0.5 text-[11px] text-gray-600'>{liner1}</p>
    </div>
    <div>
      <div className='font-jetbrains-mono text-xl font-semibold tabular-nums leading-tight text-gray-900'>{value2}</div>
      <p className='mb-0 mt-0.5 text-[11px] text-gray-600'>{liner2}</p>
    </div>
  </div>
);

const OverviewServiceCard = ({ benefitType = '', contractId, startDate, endDate, totalCustomerCount = 0, classes = '', isRunningContract = false, setUilizationToBenefit }) => {
  const [state, setState] = useState({ loading: true })
  const history = useHistory()
  const cardRef = useRef(null)
  const service = BENEFIT_TYPE_MAP[benefitType] ?? benefitType
  const details = SERVICE_CARD_DETAILS[service]
  const digitization = sessionStorage.getItem('digitization');
  const digitization_disable = digitization == 'digitization_disable';
  useEffect(() => {
    if (!contractId) return

    setState(prevState => { return { ...prevState, loading: true } })
    details['requestApi']({ contract_id: contractId, start_date: startDate, end_date: endDate },
      (res) => {
        setState({ ...res.data, loading: false })
      },
      (err) => handleAppError(err)
    )
  }, [contractId])

  const getColor = (number) => {
    number = parseInt(number, 10)
    switch (true) {
      case number <= 515: return '#E8432C'
      case number >= 516 && number <= 670: return '#FF9800'
      case number >= 671: return '#14866D'
    }
  }

  const getServiceData = (service) => {
    const { sponsored_appointments = 0, sponsored_spendings = 0,
      sponsored_utilized_employees_count = 0, sponsored_ratings = {}, utilized_employees_count = 0, consultations_count = 0,
      consultations_amount = 0, consultations_ratings = {}, hra_completed_count = 0, current_health_score = 0, optimum_health_score = 0, ready_to_book = 0,
      awaiting_approval = 0, ongoing_requests = 0, completed_requests = 0 } = state

    var title = service?.replace(/_/g, ' ') ?? ''
    var totalEmployeeCount = state?.total_employee_count ?? totalCustomerCount

    switch (service) {
      case 'consultations':
        title = 'Doctor consultation'
        var completed = utilized_employees_count ?? 0
        var availedAppointments = commaingNumber(consultations_count)
        var availedSpendings = '₹' + commaingNumber(parseInt(consultations_amount, 10))
        var ratingsData = computeRating(consultations_ratings) ?? 0
        var utilization = 0
        if (completed > 0) {
          utilization = totalEmployeeCount > 0 ? Math.round((completed * 100) / totalEmployeeCount) : 0
          setUilizationToBenefit(utilization)
        }
        return { completed, availedAppointments, availedSpendings, ratingsData, title, utilization }
      case 'hra':
        title = 'Health Risk Assessment (HRA)'
        var completed = hra_completed_count ?? 0
        var availedAppointments = commaingNumber(current_health_score)
        availedAppointments = <span className="font-semibold" style={{ color: getColor(availedAppointments) }}>{availedAppointments}</span>
        var availedSpendings = commaingNumber(optimum_health_score)
        availedSpendings = <span className="font-semibold" style={{ color: getColor(availedSpendings) }}>{availedSpendings}</span>
        var utilization = 0
        if (completed > 0) {
          utilization = totalEmployeeCount > 0 ? Math.round((completed * 100) / totalEmployeeCount) : 0
          setUilizationToBenefit(utilization)
        }
        return { completed, availedAppointments, availedSpendings, ratingsData, title, utilization }
      case 'pre_employment':
        title = 'Pre employment'
        var completed = completed_requests ?? 0
        var availedAppointments = ready_to_book
        var availedSpendings = awaiting_approval
        return { ongoing_requests, completed, availedAppointments, availedSpendings, title }
      default:
        title = title.charAt(0).toUpperCase() + title.slice(1)
        var completed = sponsored_utilized_employees_count ?? 0
        var availedAppointments = commaingNumber(sponsored_appointments)
        var availedSpendings = '₹' + commaingNumber(parseInt(sponsored_spendings, 10))
        var ratingsData = computeRating(sponsored_ratings) ?? 0
        var utilization = 0
        if (completed > 0) {
          utilization = totalEmployeeCount > 0 ? Math.round((completed * 100) / totalEmployeeCount) : 0
          setUilizationToBenefit(utilization)
        }
        return { completed, availedAppointments, availedSpendings, ratingsData, title, utilization }
    }
  }

  const dataCardsTitles = (details.cardTiles ?? []).map((t) => formatCardTileLabel(t));
  const dataCardsLiners = details['cardLiners'](isRunningContract)
  const serviceData = getServiceData(service)
  const { sponsored = false, loading } = state
  const { completed, availedAppointments, availedSpendings, ratingsData = [0, 0], title = '', ongoing_requests, utilization } = serviceData
  const [avgRating, totalRatings] = ratingsData
  const totalEmployeeCount = state?.total_employee_count ?? totalCustomerCount
  const donutColor = SERVICE_DONUT_COLORS[service] ?? '#1D6FA4'
  const donutWord = service === 'hra' ? 'completed' : 'utilised'

  if (![null, undefined].includes(utilization) && completed < 1) {
    return (
      <div
        className={`rounded-[14px] border border-gray-300 bg-white px-6 py-5 ${classes}`}
      >
        <div className='flex min-w-0 gap-3'>
          <ServiceMonogram service={service} />
          <div className='min-w-0'>
            <div className='flex flex-wrap items-center gap-2'>
              <p className='mb-0 max-w-full text-sm font-semibold leading-snug text-gray-900'>{title}</p>
              {sponsored && (
                <span className='inline-flex shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[.06em] text-green-700'>
                  Sponsored
                </span>
              )}
            </div>
            <p className='mb-0 mt-1 text-sm leading-relaxed text-gray-600'>This feature has not been utilized</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      className={`rounded-[14px] border border-gray-300 bg-white px-6 py-5 ${classes}`}
    >
      {!loading ?
        <div className='grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-center xl:grid-cols-[2fr_1.5fr_1.3fr_1.3fr] xl:gap-5'>
          <div className='flex min-w-0 gap-3'>
            <ServiceMonogram service={service} className='mt-0.5' />
            <div className='min-w-0'>
              <div className='flex flex-wrap items-center gap-2'>
                <p className='mb-0 max-w-full text-sm font-semibold leading-snug text-gray-900'>{title}</p>
                {sponsored && (
                  <span className='inline-flex shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[.06em] text-green-800'>
                    Sponsored
                  </span>
                )}
                <DownloadPngButton nodeRef={cardRef} fileName={title.toLowerCase().replace(/[^a-z0-9]+/g, '-') || benefitType} />
              </div>
              {!['hra', 'pre_employment'].includes(service) && !!totalRatings && (
                <p className='mb-0 mt-1 text-xs leading-relaxed text-gray-600'>
                  <span className='font-jetbrains-mono tabular-nums'>{avgRating}</span>
                  {'/5 · '}
                  <span className='font-jetbrains-mono tabular-nums'>{commaingNumber(totalRatings)}</span>
                  {' verified ratings'}
                </p>
              )}
              {details['btns'] && (
                <div className='mt-2 flex flex-wrap gap-x-3 gap-y-1' data-download-ignore='true'>
                  {details['btns'].map((item) => (
                    <Fragment key={item.text}>
                      {digitization_disable && item?.link === '/test-results' ? null : (
                        <button
                          type='button'
                          className='inline-flex cursor-pointer border-0 bg-transparent p-0 text-xs font-semibold text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 rounded-sm'
                          onClick={() => history.push(item.link)}
                        >
                          {item.text} →
                        </button>
                      )}
                    </Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className='min-w-0'>
            {service === 'pre_employment' ? (
              <div>
                <p className='mb-0 text-[11px] font-semibold uppercase tracking-[.06em] text-gray-600'>{dataCardsTitles[0]}</p>
                <div className='mt-1.5'>
                  <SplitStat
                    value1={commaingNumber(ongoing_requests)}
                    liner1='Ongoing'
                    value2={commaingNumber(completed)}
                    liner2='Completed'
                  />
                </div>
              </div>
            ) : (
              <div className='flex items-center gap-3'>
                <DonutRing
                  percent={totalEmployeeCount > 0 ? (completed * 100) / totalEmployeeCount : 0}
                  size={64}
                  thickness={9}
                  color={donutColor}
                  ariaLabel={`${title} utilization`}
                />
                <div className='min-w-0'>
                  <p className='mb-0 text-xs text-gray-700'>
                    <b className='font-jetbrains-mono font-semibold tabular-nums'>{commaingNumber(completed)}</b>
                    {' / '}
                    <span className='font-jetbrains-mono tabular-nums'>{commaingNumber(totalEmployeeCount)}</span>
                    {` ${donutWord}`}
                  </p>
                  {details['donutCardLiner'] && (
                    <p className='mb-0 mt-0.5 text-[10px] leading-snug text-gray-500'>{details['donutCardLiner']}</p>
                  )}
                </div>
              </div>
            )}
          </div>
          <StatBlock
            label={dataCardsTitles[1]}
            value={availedAppointments}
            caption={dataCardsLiners[0]}
            linkText={details['viewDetailsLink'] ? 'View detailed breakup' : ''}
            onLink={details['viewDetailsLink'] ? () => history.push(details['viewDetailsLink'], { contractId }) : undefined}
          />
          <StatBlock
            label={dataCardsTitles[2]}
            value={availedSpendings}
            caption={dataCardsLiners[1]}
          />
        </div> :
        <div className='h-28 flex items-center justify-center'><SpinnerDotted /></div>
      }
    </div>
  )
}

export default OverviewServiceCard
